/**
 * Créatures COF2 - générateur à la volée depuis la table creation_a_la_volee
 * du fichier creatures.json.
 *
 * Le MJ choisit une taille et un archétype (Inférieur / Standard / Puissant).
 * Le générateur retourne un profil complet (DEF, PV, attaque, DM, caracs).
 *
 * Les tables sont chargées **dynamiquement** depuis `data/loaders.loadCreatures`
 * pour éviter de figer les valeurs dans le code. Ce module fournit seulement
 * la logique de résolution.
 */

export type CreatureTaille =
  | 'Minuscule'
  | 'Très petite'
  | 'Petite'
  | 'Moyenne'
  | 'Grande'
  | 'Énorme'
  | 'Colossale';

export type CreatureArchetype = 'Inférieur' | 'Standard' | 'Puissant';

export interface CreatureProfil {
  readonly taille: CreatureTaille;
  readonly archetype: CreatureArchetype;
  readonly ncMini: string; // ex: "1/2", "3", "12"
  readonly def: number;
  readonly pv: number;
  readonly att: string; // ex: "+6" ou "+8"
  readonly dm: string; // ex: "2d6+4"
  readonly caracs: {
    readonly AGI: string;
    readonly CON: string;
    readonly FOR: string;
  };
}

/**
 * Entrée brute lue depuis creatures.json -> creation_a_la_volee.archetypes_par_taille.
 * Le schéma Zod est permissif, on normalise ici.
 */
export interface CreationATablePlaceEntry {
  readonly taille: CreatureTaille;
  readonly archetype: CreatureArchetype;
  readonly nc_mini: string;
  readonly AGI: string;
  readonly CON: string;
  readonly FOR: string;
  readonly def: number;
  readonly pv: number;
  readonly att: string;
  readonly dm: string;
}

/**
 * Résout un profil depuis la table et les 2 critères taille+archétype.
 * Retourne null si la combinaison n'existe pas.
 */
export function findProfil(
  table: readonly CreationATablePlaceEntry[],
  taille: CreatureTaille,
  archetype: CreatureArchetype,
): CreatureProfil | null {
  const entry = table.find((e) => e.taille === taille && e.archetype === archetype);
  if (!entry) return null;
  return {
    taille: entry.taille,
    archetype: entry.archetype,
    ncMini: entry.nc_mini,
    def: entry.def,
    pv: entry.pv,
    att: entry.att,
    dm: entry.dm,
    caracs: {
      AGI: entry.AGI,
      CON: entry.CON,
      FOR: entry.FOR,
    },
  };
}

/**
 * Liste les combinaisons taille x archétype disponibles dans une table.
 */
export function listAvailableProfils(
  table: readonly CreationATablePlaceEntry[],
): readonly { taille: CreatureTaille; archetype: CreatureArchetype; ncMini: string }[] {
  return table.map((e) => ({
    taille: e.taille,
    archetype: e.archetype,
    ncMini: e.nc_mini,
  }));
}

/**
 * Initiative d'une créature : 10 + PER (+3 si PER supérieure - marqué "*" dans la BDD).
 */
export function creatureInitiative(perRaw: string | undefined): number {
  if (!perRaw) return 10;
  const isSuperior = perRaw.includes('*');
  const numeric = Number.parseInt(perRaw.replace(/[^-\d]/g, ''), 10);
  const per = Number.isFinite(numeric) ? numeric : 0;
  return 10 + per + (isSuperior ? 3 : 0);
}

/**
 * Parse le NC minimum en nombre (pour tri).
 * "1/2" -> 0.5, "3" -> 3, "12" -> 12.
 */
export function parseNc(nc: string): number {
  const trimmed = nc.trim();
  if (trimmed.includes('/')) {
    const [num, den] = trimmed.split('/').map((s) => Number.parseInt(s, 10));
    if (num !== undefined && den !== undefined && den !== 0) return num / den;
  }
  const n = Number.parseInt(trimmed, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Trie les créatures par NC croissant (pour l'UI bestiaire).
 */
export function sortByNc<T extends { nc?: string | number }>(list: readonly T[]): T[] {
  const out = [...list];
  out.sort((a, b) => {
    const na = typeof a.nc === 'number' ? a.nc : parseNc(String(a.nc ?? ''));
    const nb = typeof b.nc === 'number' ? b.nc : parseNc(String(b.nc ?? ''));
    return na - nb;
  });
  return out;
}
